// =============================================================================
// MeuDriver — Full Notifications Page (shared between student and instructor)
// =============================================================================

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Bell, CheckCheck, Megaphone, Calendar, MessageCircle, Star,
  X, BookOpen, DollarSign, ArrowLeft,
} from 'lucide-react'
import { formatDistanceToNow, parseISO, isToday, isYesterday, isAfter, subDays, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useNotifications } from '@/hooks/useNotifications'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import type { NotificationRow } from '@/types/database'

// ─── Buckets ─────────────────────────────────────────────────────────────────
//
// Mapeamento dos `notifications.type` em categorias visíveis para o usuário.
// Tipos não listados caem em "Sistema" (default seguro).

type Bucket = 'all' | 'lessons' | 'payments' | 'messages' | 'system'

function bucketOf(type: string | null | undefined): Exclude<Bucket, 'all'> {
  if (!type) return 'system'
  if (type.startsWith('booking_')) return 'lessons'
  if (type === 'new_message') return 'messages'
  if (type === 'new_review' || type === 'review_received') return 'lessons'
  if (type === 'payout_released' || type.startsWith('payment_')) return 'payments'
  return 'system'
}

// ─── Icon by type ─────────────────────────────────────────────────────────────

function NotifIcon({ type }: { type: string }) {
  const base = 'h-5 w-5'
  switch (type) {
    case 'booking_confirmed':   return <Calendar       className={cn(base, 'text-brand-500')} />
    case 'booking_started':     return <BookOpen       className={cn(base, 'text-teal-500')} />
    case 'booking_completed':   return <CheckCheck     className={cn(base, 'text-green-500')} />
    case 'booking_cancelled':   return <X              className={cn(base, 'text-red-500')} />
    case 'booking_reminder':    return <Calendar       className={cn(base, 'text-orange-500')} />
    case 'new_message':         return <MessageCircle  className={cn(base, 'text-brand-500')} />
    case 'new_review':          return <Star           className={cn(base, 'text-yellow-500')} />
    case 'review_received':     return <Star           className={cn(base, 'text-orange-400')} />
    case 'payout_released':     return <DollarSign     className={cn(base, 'text-green-600')} />
    case 'instructor_approved': return <CheckCheck     className={cn(base, 'text-teal-600')} />
    default:                    return <Megaphone      className={cn(base, 'text-muted-foreground')} />
  }
}

// ─── Single notification row ──────────────────────────────────────────────────

function NotifRow({ n, onRead }: { n: NotificationRow; onRead: (id: string) => void }) {
  const unread = !n.read_at
  const timeAgo = formatDistanceToNow(parseISO(n.created_at), { addSuffix: true, locale: ptBR })
  const fullDate = format(parseISO(n.created_at), "d 'de' MMM 'às' HH:mm", { locale: ptBR })

  const inner = (
    <div
      className={cn(
        'flex items-start gap-4 px-4 py-4 sm:px-6 transition-colors',
        unread ? 'bg-brand-50/50 hover:bg-brand-50' : 'hover:bg-muted/30',
      )}
      onClick={() => unread && onRead(n.id)}
    >
      <div className={cn(
        'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
        unread ? 'bg-white shadow-sm border border-gray-100' : 'bg-muted',
      )}>
        <NotifIcon type={n.type ?? ''} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn('text-sm leading-snug', unread ? 'font-semibold text-gray-900' : 'font-medium text-muted-foreground')}>
          {n.title ?? 'Notificação'}
        </p>
        {n.body && (
          <p className="text-sm text-muted-foreground mt-0.5">{n.body}</p>
        )}
        <p className="text-xs text-muted-foreground/60 mt-1.5" title={fullDate}>{timeAgo}</p>
      </div>

      {unread && (
        <div className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-brand-500" />
      )}
    </div>
  )

  if (n.link) {
    return (
      <Link to={n.link} onClick={() => onRead(n.id)} className="block border-b border-gray-100 cursor-pointer">
        {inner}
      </Link>
    )
  }
  return (
    <div className={cn('border-b border-gray-100', unread && 'cursor-pointer')}>
      {inner}
    </div>
  )
}

// ─── Date group label ─────────────────────────────────────────────────────────

function GroupLabel({ label }: { label: string }) {
  return (
    <div className="px-4 sm:px-6 py-2 bg-muted/40 border-b border-gray-100">
      <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60">
        {label}
      </span>
    </div>
  )
}

// ─── Group notifications by date ──────────────────────────────────────────────

type Group = { label: string; items: NotificationRow[] }

function groupByDate(list: NotificationRow[]): Group[] {
  const weekAgo = subDays(new Date(), 7)
  const groups: Group[] = [
    { label: 'Hoje', items: [] },
    { label: 'Ontem', items: [] },
    { label: 'Esta semana', items: [] },
    { label: 'Mais antigas', items: [] },
  ]
  for (const n of list) {
    const d = parseISO(n.created_at)
    if (isToday(d)) groups[0].items.push(n)
    else if (isYesterday(d)) groups[1].items.push(n)
    else if (isAfter(d, weekAgo)) groups[2].items.push(n)
    else groups[3].items.push(n)
  }
  return groups.filter((g) => g.items.length > 0)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

// ─── Tab content (lista filtrada agrupada por data) ──────────────────────────

function TabList({
  items,
  onRead,
  emptyLabel,
}: {
  items: NotificationRow[]
  onRead: (id: string) => void
  emptyLabel: string
}) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 mb-4">
          <Bell className="h-7 w-7 text-gray-300" />
        </div>
        <p className="text-base font-semibold text-gray-700">{emptyLabel}</p>
        <p className="text-sm text-muted-foreground mt-1">Você está em dia com tudo!</p>
      </div>
    )
  }

  const groups = groupByDate(items)
  return (
    <div>
      {groups.map((group) => (
        <div key={group.label}>
          <GroupLabel label={group.label} />
          {group.items.map((n) => (
            <NotifRow key={n.id} n={n} onRead={onRead} />
          ))}
        </div>
      ))}
      <div className="px-4 sm:px-6 py-3 bg-gray-50 text-center">
        <p className="text-[11px] text-muted-foreground">
          Mostrando {items.length} notificação{items.length > 1 ? 'ões' : ''}
        </p>
      </div>
    </div>
  )
}

export default function NotificationsPage() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const { profile } = useAuth()
  const [activeTab, setActiveTab] = useState<Bucket>('all')
  const backPath = profile?.role === 'instructor' ? '/instrutor/dashboard' : '/aluno/dashboard'

  async function handleRead(id: string) {
    try { await markAsRead(id) } catch { /* silent */ }
  }

  async function handleMarkAll() {
    try { await markAllAsRead() } catch { /* silent */ }
  }

  const filtered = activeTab === 'all'
    ? notifications
    : notifications.filter((n) => bucketOf(n.type) === activeTab)

  // Contadores por aba (não lidas)
  const unreadByBucket = {
    all: unreadCount,
    lessons:  notifications.filter((n) => !n.read_at && bucketOf(n.type) === 'lessons').length,
    payments: notifications.filter((n) => !n.read_at && bucketOf(n.type) === 'payments').length,
    messages: notifications.filter((n) => !n.read_at && bucketOf(n.type) === 'messages').length,
    system:   notifications.filter((n) => !n.read_at && bucketOf(n.type) === 'system').length,
  }

  const tabDefs: { key: Bucket; label: string }[] = [
    { key: 'all',      label: 'Todas' },
    { key: 'lessons',  label: 'Aulas' },
    { key: 'payments', label: 'Pagamentos' },
    { key: 'messages', label: 'Mensagens' },
    { key: 'system',   label: 'Sistema' },
  ]

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="shrink-0">
          <Link to={backPath}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Notificações
          </h1>
          {unreadCount > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {unreadCount} não lida{unreadCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAll}
            className="gap-1.5 shrink-0"
          >
            <CheckCheck className="h-4 w-4" />
            Marcar todas
          </Button>
        )}
      </div>

      {/* Tabs + content */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Bucket)}>
        <TabsList className="grid w-full grid-cols-5">
          {tabDefs.map(({ key, label }) => {
            const cnt = unreadByBucket[key]
            return (
              <TabsTrigger key={key} value={key} className="text-xs sm:text-sm">
                <span>{label}</span>
                {cnt > 0 && (
                  <span className="ml-1.5 rounded-full bg-brand-500 text-white px-1.5 py-0.5 text-[10px] font-bold">
                    {cnt}
                  </span>
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>

        {tabDefs.map(({ key, label }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm">
              <TabList
                items={key === 'all' ? notifications : filtered}
                onRead={handleRead}
                emptyLabel={
                  key === 'all'
                    ? 'Nenhuma notificação'
                    : `Nenhuma notificação em ${label}`
                }
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
